/**
 * TRFer-audience adapter: staff chat with the caseload copilot (the williams
 * agent), which staff know as William (#3066).
 * Reached through `resolveChatConfig('trfer')` (#1731, spec §4.1).
 *
 * The name is an audience word, so it is named here, outside the core; its
 * value comes from `COPILOT_NAME`.
 */
import type { BrandChatConfig } from '@core/config/types';
import { useAuth } from '@/contexts/AuthContext';
import { williamsApi } from '@/services/williamsApi';
import { voiceApi } from '@/services/voiceApi';
import { trferCopy } from './copy/trfer';
import { COPILOT_NAME } from '@/lib/companion-config';
import { toCoreConversationSummary } from '@/features/chat-trf/adapter/conversations';
import {
  assertGrant,
  getApiBase,
  getApiOrigin,
  getAuthHeaders,
  refreshAuthToken,
  toVoiceConversation,
  toVoiceMintError,
} from './endpoints';

export const WILLIAMS_NAME = COPILOT_NAME;

export function trferChatConfig(): BrandChatConfig {
  return {
    brand: 'trfer',

    capabilities: {
      dictation: true,
      upload: true,
      // No william-voice seams until #1731 phase 9: neither
      // agents/william-voice/core/handoff_context.py nor text_input.py exists,
      // so a `true` here would claim a PiP and a duplex lane nothing feeds
      // (§10.8 capabilities track producers; card B12). Phase 9.1 flips them
      // with the seams. Nothing consumes this config yet, so this is a truth
      // fix, not a behaviour change.
      voicePip: false,
      duplex: false,
      actions: { copy: true, retry: true, feedback: false },
      historyDrawer: true,
      export: true,
      chips: false,
      richCards: 'off',
      recallLine: false,
    },

    // Same as the companion: no server document writer exists yet (B-4).
    serverExportFormats: [],

    copy: trferCopy(),

    endpoints: {
      getApiOrigin,
      getApiBase,
      getAuthHeaders,
      refreshAuthToken,
      getVoiceConversation: async (conversationId) =>
        toVoiceConversation(await williamsApi.getHistory(conversationId)),
      mintVoiceGrant: async (opts) => {
        try {
          const grant = await voiceApi.getTrferToken({
            conversationId: opts.roomName ?? opts.sourceConversationId ?? undefined,
            sourceConversationId: opts.sourceConversationId ?? undefined,
          });
          return assertGrant(grant);
        } catch (error) {
          throw toVoiceMintError(error);
        }
      },
      conversations: {
        list: async () => (await williamsApi.getConversations()).map(toCoreConversationSummary),
        listVoice: async () => [],
        // Phase 9 wires the TRFer surface; until then this is reachable only
        // by mounting the drawer for that audience, and it must be loud.
        delete: async () => {
          throw new Error('[#1731] the TRFer adapter has no conversation delete yet — wire it with the phase-9 surface.');
        },
      },
    },

    routes: {
      chat: '/trfer/williams',
      chatHistory: '/trfer/williams',
    },

    hooks: {
      useCurrentUserEmail: () => useAuth().user?.email,
    },

    cards: {},

    agentDisplayName: () => WILLIAMS_NAME,
  };
}
